import { FC, useState } from "react";

import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { getSurveyTranslations } from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate, useParams } from "react-router-dom";

import {
  isEnglishCode,
  languageCodeToTranslateLocale,
  REPORT_LANGUAGES_MAP,
} from "@/common/constants/languages";
import TranslatedNamesPanel from "@/components/feedback/components/TranslatedNamesPanel";
import { getCollectionById } from "@/components/feedback/data/feedbackCollectionFixtures";

// DUMMY: stand-in list of forms that will eventually come from the form builder.
// Replace with the real form-builder options once that integration exists.
const FORM_BUILDER_OPTIONS = [
  "Citizen Satisfaction Survey",
  "General Feedback Form",
  "Service Experience Survey",
];

// Languages a survey name can be translated into (English variants excluded).
const TRANSLATION_LANGUAGES = REPORT_LANGUAGES_MAP.filter(
  (lang) => !isEnglishCode(lang.code),
);

const translateName = async (
  text: string,
  langCode: string,
): Promise<string | undefined> => {
  const res = await getSurveyTranslations({
    body: {
      source_strings: [{ text }],
      target_language: languageCodeToTranslateLocale(langCode),
    },
  });
  return res.data?.translations?.[0]?.translations?.[0]?.text;
};

// Survey detail / editor. For now it supports renaming the survey, picking the
// underlying form, and translating the name — name/form are local state (no
// persistence yet); translations call the shared survey-translation endpoint.
const SurveyDetailPage: FC = () => {
  const navigate = useNavigate();
  const { collectionId } = useParams();
  const collection = collectionId ? getCollectionById(collectionId) : undefined;

  const [name, setName] = useState(collection?.name ?? "");
  const [form, setForm] = useState("");
  const [translatedNames, setTranslatedNames] = useState<
    Record<string, string>
  >({});
  const [selectedLang, setSelectedLang] = useState("");
  const [translatingLang, setTranslatingLang] = useState(false);
  const [translatingAll, setTranslatingAll] = useState(false);

  const handleTranslateLang = async () => {
    if (!selectedLang || !name.trim()) return;
    setTranslatingLang(true);
    try {
      const translated = await translateName(name.trim(), selectedLang);
      if (translated) {
        setTranslatedNames((prev) => ({ ...prev, [selectedLang]: translated }));
      } else {
        toast.warning("No translation returned");
      }
    } catch {
      toast.error("Translation failed");
    } finally {
      setTranslatingLang(false);
    }
  };

  const handleSave = () => {
    // DUMMY: no persistence yet — surface a confirmation for the demo. Once the
    // feedback backend exists this saves name/form/translated_names.
    toast.success("Survey saved");
  };

  const handleTranslateAll = async () => {
    if (!name.trim()) return;
    setTranslatingAll(true);
    try {
      const pending = TRANSLATION_LANGUAGES.filter(
        (lang) => !translatedNames[lang.code]?.trim(),
      );
      if (pending.length === 0) {
        toast.info("All languages already translated");
        return;
      }
      for (const lang of pending) {
        const translated = await translateName(name.trim(), lang.code);
        if (translated) {
          setTranslatedNames((prev) => ({ ...prev, [lang.code]: translated }));
        }
      }
      toast.success("Translations updated");
    } catch {
      toast.error("Translation failed");
    } finally {
      setTranslatingAll(false);
    }
  };

  return (
    <Box
      sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Box sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("..")}>
          Back to Surveys
        </Button>
      </Box>

      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>
            Survey Settings
          </Typography>
          <Stack spacing={3} sx={{ maxWidth: 520 }}>
            <TextField
              label="Survey Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
            />
            <TextField
              select
              label="Form"
              value={form}
              onChange={(event) => setForm(event.target.value)}
              helperText="Select the form this survey is built from"
              fullWidth
            >
              {FORM_BUILDER_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Paper>

        <TranslatedNamesPanel
          sourceText={name}
          languages={TRANSLATION_LANGUAGES}
          translatedNames={translatedNames}
          onTranslatedNamesChange={setTranslatedNames}
          selectedLang={selectedLang}
          onSelectedLangChange={setSelectedLang}
          translatingLang={translatingLang}
          translatingAll={translatingAll}
          onTranslateLang={handleTranslateLang}
          onTranslateAll={handleTranslateAll}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button onClick={() => navigate("..")}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!name.trim()}
          >
            Save Details
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default SurveyDetailPage;
